import type { QueryKey } from "@tanstack/react-query";

/** Query-key factory for the brokerage (firm) entity. Co-locating the key
 * literals here satisfies the stale-after-save gate (queryKey array literals
 * must live in a `*Keys` file). The `production` read nests under
 * `brokerageKeys.all` (`["brokerages", …]`).
 *
 * NOTE: the pre-existing BrokerageDetailPage detail/list reads still use inline
 * LEGACY keys — `["brokerage", id]` (singular) for detail and `["brokerages"]`
 * for the list — that predate this factory. The singular detail key is NOT
 * nested under `brokerageKeys.all`, so a future mutation must invalidate those
 * legacy keys too (don't assume `brokerageKeys.all` refreshes the detail
 * header) until they're migrated onto this factory. */
export const brokerageKeys = {
  all: ["brokerages"] as const,
  details: () => [...brokerageKeys.all, "detail"] as const,
  detail: (id: string) => [...brokerageKeys.details(), id] as const,
  production: (id: string): QueryKey => [...brokerageKeys.detail(id), "production"],

  // Brokerage Certification & Pipeline. The pipeline board nests under `all`
  // (so invalidating `all` refreshes it). `legacyDetail` / `stageReadiness`
  // are the pre-existing inline keys the detail page + stage-advance panel use;
  // they are registered here so new code references them via the factory rather
  // than a fresh inline literal (the stale-after-save gate).
  pipeline: (): QueryKey => [...brokerageKeys.all, "pipeline"],
  legacyDetail: (id: string): QueryKey => ["brokerage", id],
  stageReadiness: (id: string): QueryKey => ["brokerage-stage-readiness", id],

  // Portal absorption (WS-D1) — the firm's linked portal login lookup.
  portalAccount: (id: string): QueryKey => [...brokerageKeys.detail(id), "portal-account"],
};
